import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { Platform } from 'react-native'
import * as WebBrowser from 'expo-web-browser'
import type { Session } from '@supabase/supabase-js'
import { supabase } from '../../lib/supabase'
import { logger } from '../../helpers/logger.helper'
import {
  getAllSavedSessions,
  saveSession,
  getStoredSession,
  removeStoredSession,
  type SavedAccountSession,
} from '../helpers/accountSessions.storage'
import {
  getOAuthRedirectUrls,
  parseSupabaseOAuthRedirectUrl,
} from '../../signIn/helpers/signInApi'
import { GOOGLE_OAUTH_QUERY_PARAMS } from '../../signIn/constants/oauth.constants'
import { exchangeOAuthCodeForSessionOnce } from '../../signIn/helpers/oauthPkceExchange.helper'
import { API_URL, API_ENDPOINTS } from '../../../constants/api'
import { useAvatarContext } from '../../avatars/contexts/AvatarContext'
import type { EquippedMap } from '../../../components/avatars/LayeredAvatarLab/characterCustomizer.types'

export type { SavedAccountSession }

export interface AccountSwitcherValue {
  /** All accounts stored on this device (including the currently active one). */
  accounts: SavedAccountSession[]
  /** userId of the currently active Supabase session. */
  activeUserId: string | null
  /** True while a switch or add-account is in flight. */
  loading: boolean
  /**
   * Restore a stored session for a different userId.
   * Calls `supabase.auth.setSession()` → `SupabaseAuthContext` reacts and the whole app switches.
   */
  switchAccount: (userId: string) => Promise<void>
  /**
   * Launch an OAuth flow to sign in with a second Google account.
   * Native: opens an in-app browser tab (current session stays intact until `setSession` is called).
   * Web: saves current session, then triggers the normal OAuth redirect; after redirect both
   *      sessions are in storage and the switcher shows both.
   */
  addAccount: () => Promise<void>
  /** Remove a stored account. If it is the active account, switches to the next one or signs out. */
  removeAccount: (userId: string) => Promise<void>
}

const defaultValue: AccountSwitcherValue = {
  accounts: [],
  activeUserId: null,
  loading: false,
  switchAccount: async () => {},
  addAccount: async () => {},
  removeAccount: async () => {},
}

const AccountSwitcherContext = createContext<AccountSwitcherValue>(defaultValue)

function mapSessionToSaved(session: Session, existing?: SavedAccountSession | null): SavedAccountSession {
  const meta = session.user.user_metadata ?? {}
  return {
    userId: session.user.id,
    email: session.user.email ?? '',
    displayName: ((meta.full_name || meta.name) as string | null | undefined) ?? null,
    avatarUrl: ((meta.avatar_url) as string | null | undefined) ?? null,
    zportiAvatarId: existing?.zportiAvatarId ?? null,
    zportiAvatarConfig: existing?.zportiAvatarConfig ?? null,
    accessToken: session.access_token,
    refreshToken: session.refresh_token ?? '',
    expiresAt: session.expires_at != null ? session.expires_at * 1000 : 0,
  }
}

/** Fetch the Zporti game avatar (ready id + custom look) for `userId` using the provided Bearer token. */
async function fetchZportiAvatar(
  userId: string,
  accessToken: string,
): Promise<{ avatarId: string | null; avatarConfig: EquippedMap | null }> {
  try {
    const res = await fetch(`${API_URL}${API_ENDPOINTS.users.profile}/${userId}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
    if (!res.ok) return { avatarId: null, avatarConfig: null }
    const json = (await res.json()) as {
      data?: { profile?: { avatarId?: unknown; avatarConfig?: unknown } }
    }
    const rawId = json?.data?.profile?.avatarId
    const rawConfig = json?.data?.profile?.avatarConfig
    return {
      avatarId: typeof rawId === 'string' && rawId.length > 0 ? rawId : null,
      avatarConfig: rawConfig != null && typeof rawConfig === 'object' ? (rawConfig as EquippedMap) : null,
    }
  } catch {
    return { avatarId: null, avatarConfig: null }
  }
}

/**
 * Manages multiple Zporti auth identities on one device (e.g., personal Gmail + work Google account).
 *
 * Persists sessions via AsyncStorage / localStorage keyed by userId.
 * On `switchAccount`, calls `supabase.auth.setSession(storedTokens)` which fires the
 * `onAuthStateChange` in `SupabaseAuthContext`, cascading a full app refresh to the new identity.
 *
 * Mount inside `SupabaseAuthProvider` (needs the Supabase client; doesn't need the React context,
 * just the singleton client so it can subscribe to auth state independently).
 */
export function AccountSwitcherProvider({ children }: { children: ReactNode }) {
  const [accounts, setAccounts] = useState<SavedAccountSession[]>([])
  const [activeUserId, setActiveUserId] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const activeUserIdRef = useRef<string | null>(null)
  const { currentAvatarId, currentAvatarConfig } = useAvatarContext()

  const reloadAccounts = useCallback(async () => {
    const all = await getAllSavedSessions(activeUserIdRef.current)
    setAccounts(all)
  }, [])

  /**
   * Back-fills zportiAvatarId for any stored account that doesn't have one yet.
   * Uses the active session's token (always fresh) to fetch profiles for all accounts —
   * the profile endpoint accepts any userId and just needs a valid Bearer token.
   */
  const hydrateAvatarIds = useCallback(async () => {
    const all = await getAllSavedSessions()
    const missing = all.filter((s) => !s.zportiAvatarId)
    if (missing.length === 0) return

    // Prefer the current Supabase session's token (guaranteed fresh).
    const { data: { session: activeSession } } = await supabase.auth.getSession()
    const authToken = activeSession?.access_token ?? null

    let updated = false
    await Promise.all(
      missing.map(async (session) => {
        // Use active token first; fall back to the account's own stored token.
        const token = authToken ?? session.accessToken
        const { avatarId, avatarConfig } = await fetchZportiAvatar(session.userId, token)
        if (avatarId || avatarConfig) {
          await saveSession({
            ...session,
            zportiAvatarId: avatarId ?? session.zportiAvatarId,
            zportiAvatarConfig: avatarConfig,
          })
          updated = true
        }
      }),
    )
    if (updated) await reloadAccounts()
  }, [reloadAccounts])

  const persistSession = useCallback(
    async (session: Session) => {
      const all = await getAllSavedSessions()
      const existing = all.find((s) => s.userId === session.user.id) ?? null
      const mapped = mapSessionToSaved(session, existing)

      // Fetch the Zporti game avatar once if we don't have it yet.
      if (!mapped.zportiAvatarId) {
        const { avatarId, avatarConfig } = await fetchZportiAvatar(session.user.id, session.access_token)
        if (avatarId) {
          mapped.zportiAvatarId = avatarId
        }
        mapped.zportiAvatarConfig = avatarConfig
      }

      await saveSession(mapped)
      await reloadAccounts()
    },
    [reloadAccounts],
  )

  // Subscribe to Supabase auth state changes so every new session is auto-saved.
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) return
      setActiveUserId(session.user.id)
      activeUserIdRef.current = session.user.id
      void persistSession(session)
    })

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      const newUserId = session?.user?.id ?? null
      setActiveUserId(newUserId)
      activeUserIdRef.current = newUserId
      if (session) {
        void persistSession(session)
      }
    })

    void reloadAccounts()
    void hydrateAvatarIds()
    return () => subscription.unsubscribe()
  }, [persistSession, reloadAccounts, hydrateAvatarIds])

  // Whenever AvatarContext resolves the active user's avatar (ready id or custom look),
  // sync it into storage immediately. This is always fresh (no API call needed) —
  // AvatarContext already fetched the profile. The JSON equality guard prevents a resave loop.
  useEffect(() => {
    if (!activeUserId) return
    void (async () => {
      const all = await getAllSavedSessions()
      const session = all.find((s) => s.userId === activeUserId)
      if (!session) return
      const nextAvatarId = currentAvatarId ?? session.zportiAvatarId
      const idChanged = currentAvatarId != null && session.zportiAvatarId !== currentAvatarId
      const configChanged =
        JSON.stringify(session.zportiAvatarConfig ?? null) !== JSON.stringify(currentAvatarConfig ?? null)
      if (!idChanged && !configChanged) return
      await saveSession({
        ...session,
        zportiAvatarId: nextAvatarId,
        zportiAvatarConfig: currentAvatarConfig ?? null,
      })
      await reloadAccounts()
    })()
  }, [activeUserId, currentAvatarId, currentAvatarConfig, reloadAccounts])

  const switchAccount = useCallback(async (userId: string) => {
    if (userId === activeUserIdRef.current) return
    const stored = await getStoredSession(userId)
    if (!stored) {
      logger.warn('[AccountSwitcher] No stored session for userId', { prefix: userId.slice(0, 8) })
      return
    }
    setLoading(true)
    try {
      const { error } = await supabase.auth.setSession({
        access_token: stored.accessToken,
        refresh_token: stored.refreshToken,
      })
      if (error) throw error
      // `SupabaseAuthContext.onAuthStateChange` fires automatically and refreshes the whole app.
    } catch (e) {
      logger.warn('[AccountSwitcher] switchAccount failed', {
        message: e instanceof Error ? e.message : String(e),
      })
      throw e
    } finally {
      setLoading(false)
    }
  }, [])

  const addAccount = useCallback(async () => {
    const redirectUrls = getOAuthRedirectUrls()
    if (!redirectUrls) throw new Error('No OAuth redirect URL configured')
    const { redirectUrl } = redirectUrls

    if (Platform.OS !== 'web') {
      // Native: open OAuth in an in-app browser modal.
      // The main Supabase session is unaffected until we explicitly call setSession below.
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectUrl,
          queryParams: GOOGLE_OAUTH_QUERY_PARAMS,
          skipBrowserRedirect: true,
        },
      })
      if (error) throw error
      if (!data?.url) throw new Error('OAuth URL not returned')

      const result = await WebBrowser.openAuthSessionAsync(data.url, redirectUrl)
      if (result.type !== 'success' || !result.url) return // user cancelled

      const tokens = parseSupabaseOAuthRedirectUrl(result.url)
      if (tokens) {
        const { error: sessionErr } = await supabase.auth.setSession(tokens)
        if (sessionErr) throw sessionErr
        return
      }
      if (result.url.includes('code=')) {
        const exchangeResult = await exchangeOAuthCodeForSessionOnce(result.url)
        if (exchangeResult.error) throw exchangeResult.error
      }
      return
    }

    // Web: the current session is already saved in storage (auto-saved by the effect above).
    // Trigger the normal OAuth redirect. After the round-trip, the new session is active and
    // `onAuthStateChange` saves it — both sessions are now in storage.
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: redirectUrl,
        queryParams: GOOGLE_OAUTH_QUERY_PARAMS,
      },
    })
    if (error) throw error
    if (data?.url && typeof window !== 'undefined') {
      window.location.href = data.url
    }
  }, [])

  const removeAccount = useCallback(
    async (userId: string) => {
      await removeStoredSession(userId)
      if (userId === activeUserIdRef.current) {
        const remaining = await getAllSavedSessions()
        const other = remaining.find((s) => s.userId !== userId)
        if (other) {
          await switchAccount(other.userId)
        } else {
          await supabase.auth.signOut()
        }
      }
      await reloadAccounts()
    },
    [reloadAccounts, switchAccount],
  )

  const value = useMemo<AccountSwitcherValue>(
    () => ({ accounts, activeUserId, loading, switchAccount, addAccount, removeAccount }),
    [accounts, activeUserId, loading, switchAccount, addAccount, removeAccount],
  )

  return (
    <AccountSwitcherContext.Provider value={value}>{children}</AccountSwitcherContext.Provider>
  )
}

export function useAccountSwitcher(): AccountSwitcherValue {
  return useContext(AccountSwitcherContext)
}
